import { ILayer } from '../types/ILayer';
import { ELayerType } from '../types/ELayerType';

/**
 * Inverse Distance Weighting (IDW) utilities for interpolating layer values
 * at arbitrary geographic coordinates.
 */
class IDW {
  /**
   * IDW interpolation for layers with a single value per point.
   * @param lng Longitude of the query point
   * @param lat Latitude of the query point
   * @param layer The layer containing data points
   * @param power The power parameter for IDW (typically 2-3)
   * @returns The interpolated value at the given coordinates
   */
  static interpolate(lng: number, lat: number, layer: ILayer, power: number = 3): number | null {
    if (!layer || !layer.data || layer.data.length === 0) return null;

    let top = 0;
    let bot = 0;

    for (const dp of layer.data) {
      const dist = Math.sqrt(Math.pow(dp.lng - lng, 2) + Math.pow(dp.lat - lat, 2));
      if (dist === 0) {
        // Point is exactly at a data point
        return dp.value[0];
      }
      top += dp.value[0] / Math.pow(dist, power);
      bot += 1 / Math.pow(dist, power);
    }

    if (bot === 0) {
      return top;
    }
    return top / bot;
  }

  /**
   * IDW interpolation for layers with two values per point (e.g., CurrentSpeed with U and V).
   * @param lng Longitude of the query point
   * @param lat Latitude of the query point
   * @param layer The layer containing data points
   * @param power The power parameter for IDW (typically 2-3)
   * @returns An array of two interpolated values [U, V] at the given coordinates
   */
  static interpolate2(lng: number, lat: number, layer: ILayer, power: number = 3): number[] | null {
    if (!layer || !layer.data || layer.data.length === 0) return null;

    let top = [0, 0];
    let bot = 0;

    for (const dp of layer.data) {
      const dist = Math.sqrt(Math.pow(dp.lng - lng, 2) + Math.pow(dp.lat - lat, 2));
      if (dist === 0) {
        // Point is exactly at a data point
        return [dp.value[0], dp.value[1]];
      }
      top[0] += dp.value[0] / Math.pow(dist, power);
      top[1] += dp.value[1] / Math.pow(dist, power);
      bot += 1 / Math.pow(dist, power);
    }

    if (bot === 0) {
      return top;
    }
    return [top[0] / bot, top[1] / bot];
  }

  /**
   * Interpolates layer value(s) at the given coordinates, automatically
   * choosing between single-value and two-value interpolation based on layer type.
   * @param lng Longitude of the query point
   * @param lat Latitude of the query point
   * @param layer The layer to interpolate
   * @param power The power parameter for IDW (typically 2-3)
   * @returns Single value or array of values depending on layer type
   */
  static interpolateLayer(lng: number, lat: number, layer: ILayer, power: number = 3): number | number[] | null {
    if (layer.type === ELayerType.CurrentSpeed) {
      return IDW.interpolate2(lng, lat, layer, power);
    }
    return IDW.interpolate(lng, lat, layer, power);
  }

  /**
   * Calculates magnitude from U and V components.
   */
  static magnitude(u: number, v: number): number {
    return Math.sqrt(u * u + v * v);
  }

  /**
   * Calculates direction in degrees from U and V components.
   * Returns the direction the current is flowing TO (oceanographic convention).
   * 0 = North, 90 = East, 180 = South, 270 = West
   */
  static direction(u: number, v: number): number {
    // atan2 gives angle from positive x-axis (East), counterclockwise
    // We want angle from North, clockwise
    const radians = Math.atan2(u, v); // Note: atan2(u, v) not atan2(v, u) for direction TO
    let degrees = radians * (180 / Math.PI);
    if (degrees < 0) degrees += 360;
    return degrees;
  }
}

export { IDW };
